import { isNil } from "lodash";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useYupValidationResolver } from "@ryde/redux";
import {
  CopyToSupplierExistingBehavior,
  createErrorResult,
  createSuccessResult,
  DrawerType,
  errors as errorsKeys,
  IRydeProcess,
  Log,
  PriceRateAdjustmentType,
  PriceRateApi,
  PriceRoundingBehavior,
  RydeProcessTitle,
  selectedIdsSF,
  SimpleResult,
  tableRowsAtomFamily,
  unique,
  useDrawers,
} from "@ryde/shared";

import {
  CopyToSupplierPriceRatesForm,
  copyToSupplierPriceRatesValidationSchema,
  ICopyToSupplierPriceRatesForm,
} from "./copy-to-supplier-price-rates-drawer.models";

const DEFAULT_DECIMAL_DIGITS = 2;

export const useCopyToSupplierPriceRatesDrawer = () => {
  const { t } = useTranslation();
  const selectedIds = useRecoilValue(selectedIdsSF("priceRate"));
  const tableRows = useRecoilValue(tableRowsAtomFamily("priceRate"));
  const resolver = useYupValidationResolver(
    copyToSupplierPriceRatesValidationSchema,
    "priceRate",
  );
  const { closeDrawer, openDrawer } = useDrawers();

  const defaultSupplierIds = useMemo(() => {
    if (!tableRows || !selectedIds.length) {
      return [];
    }

    const selectedIdsSet = new Set(selectedIds);
    const selectedRows = tableRows.filter(row => selectedIdsSet.has(row.id));
    const supplierIds = selectedRows
      .map(row => (row as { supplierId?: number | null }).supplierId)
      .filter((id): id is number => id !== null && id !== undefined && id > 0);

    return unique(supplierIds);
  }, [tableRows, selectedIds]);

  const hasPreSelectedSuppliers = defaultSupplierIds.length > 0;

  const methods = useForm<ICopyToSupplierPriceRatesForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      [CopyToSupplierPriceRatesForm.adjustmentType]:
        PriceRateAdjustmentType.Percentage,
      [CopyToSupplierPriceRatesForm.adjustmentValue]: 0,
      [CopyToSupplierPriceRatesForm.supplierIds]: defaultSupplierIds,
      [CopyToSupplierPriceRatesForm.existingBehavior]:
        CopyToSupplierExistingBehavior.Skip,
      [CopyToSupplierPriceRatesForm.roundingBehavior]:
        PriceRoundingBehavior.Floor,
      [CopyToSupplierPriceRatesForm.decimalDigits]: DEFAULT_DECIMAL_DIGITS,
    },
    resolver: resolver,
  });

  const {
    getValues,
    watch,
    setValue,
    formState: { errors, isValid, touchedFields },
  } = methods;

  useEffect(() => {
    if (defaultSupplierIds.length > 0) {
      setValue(CopyToSupplierPriceRatesForm.supplierIds, defaultSupplierIds, {
        shouldValidate: true,
        shouldDirty: false,
        shouldTouch: true,
      });
    }
  }, [defaultSupplierIds, setValue]);
  const supplierIdsValue = watch(CopyToSupplierPriceRatesForm.supplierIds);
  const adjustmentValue = watch(CopyToSupplierPriceRatesForm.adjustmentValue);
  const hasSupplierIds = supplierIdsValue && supplierIdsValue.length > 0;
  const hasAdjustmentValue = !isNil(adjustmentValue);
  const isSaveDisabled = Boolean(
    !isValid ||
    Object.keys(errors || {}).length ||
    !hasAdjustmentValue ||
    (!hasPreSelectedSuppliers && !touchedFields.supplierIds && !hasSupplierIds),
  );

  const onSaveClick = useEvent(async (): Promise<SimpleResult<void>> => {
    if (!isValid) {
      Log.warnAndToast(t(errorsKeys.common.validationError));
      return createErrorResult(errorsKeys.common.validationError);
    }

    const values = getValues();

    if (!tableRows || !selectedIds.length) {
      Log.warnAndToast(t(errorsKeys.common.noRecordsFoundForBulkOperation));
      return createErrorResult(
        errorsKeys.common.noRecordsFoundForBulkOperation,
      );
    }

    if (!values.supplierIds?.length) {
      Log.warnAndToast(t(errorsKeys.common.noSuppliersSelected));
      return createErrorResult(errorsKeys.common.noSuppliersSelected);
    }

    const isPercentage =
      values.adjustmentType === PriceRateAdjustmentType.Percentage;

    const result = await PriceRateApi.copyToSupplierPriceRates({
      adjustmentType: values.adjustmentType,
      adjustmentValue: values.adjustmentValue,
      supplierIds: values.supplierIds,
      existingBehavior: values.existingBehavior,
      ids: selectedIds,
      ...(isPercentage && {
        roundingBehavior: values.roundingBehavior,
        decimalDigits: values.decimalDigits,
      }),
    });

    if (!result) {
      return createErrorResult(errorsKeys.common.errorSavingToAPI);
    }

    if (result?.success) {
      const resultData = result.data as IRydeProcess;
      closeDrawer(DrawerType.CopyToSupplierPriceRates);
      openDrawer<DrawerType.ProcessDrawer>({
        drawerProps: {
          type: DrawerType.ProcessDrawer,
          processId: resultData.id,
          processTitle: RydeProcessTitle.CopyToSupplierPriceRates,
          entity: "priceRate",
        },
      });
    }

    return createSuccessResult();
  });

  return { methods, errors, isSaveDisabled, onSaveClick };
};
